import { globalAuthContext } from '@headbox-ui/auth-hook'
import * as Sentry from '@sentry/react'
import { ConfigProvider, Spin } from 'antd'
import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import { render } from 'react-dom'
import { createClient, Provider } from 'urql'
import Authenticator from './Components/Authenticator'
import ErrorBoundary from './Components/ErrorBoundary'
import { GoogleScript } from './Components/GoogleScript'
import I18n from './Components/I18n'
import {
  ASSET_SERVICE,
  BUSINESS_GATEWAY,
  HEADBOX_ROOT_SERVICE,
  JWT_COOKIE_NAME,
  APP_SENTRY_LOGGING,
  REFRESH_TOKEN_COOKIE_NAME,
  AUTH_SERVICE,
} from './Constants'
import setupAppSentry from './Constants/Sentry/setupAppSentry'
import { setHeaderSentry } from './Constants/Sentry/headerSentry'
import { CONTENT_STYLE } from './Styles'
import Router from './Views/Router'
import * as UrqlModule from 'urql'
import { createFromUrqlClient } from '@headbox-ui/auth-hook/dist/graphQLAdapter'

__webpack_public_path__ = `${ASSET_SERVICE}/`

// Store a copy of the Sentry instance from the header.
setHeaderSentry(Sentry.getCurrentHub())

if (APP_SENTRY_LOGGING) {
  setupAppSentry()
}

globalAuthContext.set({
  refreshTokenCookie: REFRESH_TOKEN_COOKIE_NAME,
  sessionTokenCookie: JWT_COOKIE_NAME,
  rootService: HEADBOX_ROOT_SERVICE,
})

const App = (): JSX.Element => {
  const [cookies] = useCookies([REFRESH_TOKEN_COOKIE_NAME, JWT_COOKIE_NAME])
  const [hasSetGlobalAuth, setHasSetGlobalAuth] = useState(false)

  const appClient = createClient({
    url: BUSINESS_GATEWAY,
    fetchOptions: () => {
      return {
        headers: { 'x-headbox-accesstoken': cookies[JWT_COOKIE_NAME] },
      }
    },
  })

  const authOptions = {
    url: AUTH_SERVICE,
    fetchOptions: () => ({ headers: { 'x-headbox-accesstoken': cookies[JWT_COOKIE_NAME] } }),
  }

  const authClient = useMemo(() => createClient(authOptions), [cookies, cookies[JWT_COOKIE_NAME]])

  useEffect(() => {
    globalAuthContext.set({
      client: createFromUrqlClient({ urqlModule: UrqlModule, client: authClient, ...authOptions }),
      refreshTokenCookie: REFRESH_TOKEN_COOKIE_NAME,
      sessionTokenCookie: JWT_COOKIE_NAME,
      rootService: HEADBOX_ROOT_SERVICE,
    })
    setHasSetGlobalAuth(true)
  }, [authClient])

  if (!hasSetGlobalAuth) return null

  return (
    <ConfigProvider>
      <I18n>
        <GoogleScript>
          <Suspense fallback={<Spin />}>
            <ErrorBoundary>
              <Provider value={appClient}>
                <Authenticator>
                  <section style={CONTENT_STYLE}>
                    <Router />
                  </section>
                </Authenticator>
              </Provider>
            </ErrorBoundary>
          </Suspense>
        </GoogleScript>
      </I18n>
    </ConfigProvider>
  )
}

render(<App />, document.querySelector('app-root'))
